import type { SceneNodeItem } from "../scene/SceneNodeItem";
import type { ItemStoreEvents } from "../itemStore.store";
import type { SceneMediumData } from "./sceneMediumData";
import { some } from "lodash-es";
import { useMediaStore } from "@core/media/media.store";
import { useSceneNodesStore } from "../scene/sceneNodes.store";

const mediumDefaults: {
  width: number;
  height: number;
  aspectRatioLocked: boolean;
} = {
  width: 30,
  height: 40,
  aspectRatioLocked: true
};

export const sceneMediumStoreHooks: Partial<ItemStoreEvents<SceneNodeItem<"medium">>> = {
  async beforeCreate(item) {
    // Apply medium data defaults
    item.title = item.title ?? "";
    item.aspectRatioLocked = item.aspectRatioLocked ?? mediumDefaults.aspectRatioLocked;

    // Apply default dimensions if not set
    item.width = item.width ?? mediumDefaults.width;
    item.height = item.height ?? mediumDefaults.height;

    if (!item.data) {
      item.data = {};
    }

    // If the medium data is set, create a new media entry to save that medium
    if (item.data.data) {
      const newMediaItem = await useMediaStore().create({
        data: item.data.data
      });
      item.data.mediaUUID = newMediaItem.uuid;
      item.data.data = undefined;
    }

    return item;
  },

  beforeUpdate(updatedMedium, changedData, mediumBefore) {
    // If the medium data changed, create a new media entry to save that medium
    if (updatedMedium.data?.data) {
      const changes: Partial<SceneNodeItem<"medium">> = {};
      
      // Remove the old media entry
      const beforeData = mediumBefore?.data as SceneMediumData | undefined;
      if (beforeData?.mediaUUID) {
        // Does any other medium still use this media?
        const sceneNodesStore = useSceneNodesStore();
        const stillInUse = some(sceneNodesStore.items, (node) => {
          if (node.dataType !== "medium") return false;
          const mediumData = node.data as SceneMediumData;
          return mediumData?.mediaUUID === beforeData.mediaUUID && 
                 node.id !== updatedMedium.id;
        });
        
        if (!stillInUse) {
          useMediaStore().delete({ uuid: beforeData.mediaUUID });
        }
      }

      // Create the new media entry and connect the medium to it
      const newMediaItem = useMediaStore().create({
        data: updatedMedium.data.data
      });

      if (!changes.data) changes.data = {} as SceneMediumData;
      (changes.data as SceneMediumData).data = undefined;
      (changes.data as SceneMediumData).mediaUUID = newMediaItem.uuid;
      return changes;
    }

    return {};
  },

  async afterDelete(deletedMedium) {
    const deletedData = deletedMedium.data as SceneMediumData;
    if (deletedData?.mediaUUID) {
      const sceneNodesStore = useSceneNodesStore();
      
      // Check if any other medium still uses this mediaUUID
      const stillInUse = some(sceneNodesStore.items, (node) => {
        if (node.dataType !== "medium") return false;
        const mediumData = node.data as SceneMediumData;
        return mediumData?.mediaUUID === deletedData.mediaUUID &&
               node.id !== deletedMedium.id;
      });

      if (!stillInUse) {
        useMediaStore().delete({ uuid: deletedData.mediaUUID });
      }
    }
  }
};
